import React, { useEffect, useState } from "react";
import { Box, Card, CardContent, Typography, Avatar, Button, TextField, Switch, FormControlLabel, Divider, CircularProgress, Alert, Grid, IconButton, FormControl, InputLabel, Select, MenuItem, Tooltip } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import useDataFetch, { usePostDataFetch } from "../../utils/useDataFetch";
import { useAuth } from '../../context/AuthContext';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate } from 'react-router-dom';

const Profil = ({ onClose }) => {

    const navigate = useNavigate();

    const { user: loggedInUser } = useAuth();
    const profileId = loggedInUser?.id;
    const isOwnProfile = !!profileId;

    const { data: profileResp, error, isLoading, refetch } = useDataFetch({ url: profileId ? `/users/profile/${profileId}` : null });
    const { postData: updateProfile, data: updateData, isLoading: isUpdating } = usePostDataFetch();
    const { postData: submitComment, data: commentData, isLoading: isCommenting } = usePostDataFetch();

    // Wir nutzen hier nativen fetch oder einen manuellen call für DELETE
    const apiUrl = process.env.REACT_APP_API_URL || "";

    const [isEditMode, setIsEditMode] = useState(false);
    const [editData, setEditData] = useState({
        biografie: '',
        bevorzugte_position: '',
        starker_fuss: '',
        bisherige_vereine: '',
        kommentare_erlaubt: true
    });

    const [newComment, setNewComment] = useState("");

    useEffect(() => {
        if (profileResp?.success && profileResp.user) {
            setEditData({
                biografie: profileResp.user.biografie || '',
                bevorzugte_position: profileResp.user.bevorzugte_position || '',
                starker_fuss: profileResp.user.starker_fuss || '',
                bisherige_vereine: profileResp.user.bisherige_vereine || '',
                kommentare_erlaubt: profileResp.user.kommentare_erlaubt === 1
            });
        }
    }, [profileResp]);

    useEffect(() => {
        if (updateData?.success || commentData?.success) {
            setIsEditMode(false);
            setNewComment("");
            refetch();
        }
    }, [updateData, commentData]);

    const handleSaveProfile = () => {
        updateProfile({ url: '/users/profile', aBody: editData, skipAuth: false });
    };

    const handleAddComment = (e) => {
        e.preventDefault();
        submitComment({ url: `/users/profile/${profileId}/comment`, aBody: { kommentar: newComment }, skipAuth: false });
    };

    const handleDeleteComment = async (commentId) => {
        if (!window.confirm("Kommentar wirklich löschen?")) return;
        try {
            const token = localStorage.getItem("token");
            await fetch(`${apiUrl}/users/comment/${commentId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            refetch();
        } catch (err) {
            console.error(err);
        }
    };

    if (!profileId) return <Alert severity="warning">Profil nicht verfügbar. Bitte melde dich an.</Alert>;
    if (isLoading) return <CircularProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!profileResp?.user) return <Alert severity="warning">Profil nicht gefunden</Alert>;

    const { user, teams, comments } = profileResp;
    const imageUrl = user.profilbild_url ? `${apiUrl}${user.profilbild_url}` : '';

    return (
        <Box sx={{ p: { xs: 2, md: 5 }, maxWidth: 900, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4">Mein Profil</Typography>
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Card>
                <CardContent>
                    <Grid container spacing={4}>
                        {/* Bild und Grund-Infos */}
                        <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <Avatar src={imageUrl} sx={{ width: 150, height: 150 }} />
                            <Typography variant="h5"><b>{user?.first_name} {user?.last_name}</b></Typography>
                            <Typography variant="body2" color="primary">{user?.role?.toUpperCase() || ''}</Typography>

                            {isOwnProfile && !isEditMode && (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button variant="contained" onClick={() => setIsEditMode(true)}>Profil bearbeiten</Button>

                                    <Tooltip title="Einstellungen">
                                        <IconButton color="inherit" onClick={() => { navigate('/settings'); onClose(); }} sx={{ p: 1 }}>
                                            <SettingsIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            )}
                            {isOwnProfile && isEditMode && (
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button variant="contained" color="success" onClick={handleSaveProfile} disabled={isUpdating}>Speichern</Button>
                                    <Button variant="outlined" color="error" onClick={() => setIsEditMode(false)}>Abbrechen</Button>
                                </Box>
                            )}
                        </Grid>

                        {/* Profildaten */}
                        <Grid size={{ xs: 12, md: 8 }}>
                            {isEditMode ? (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <TextField label="Über mich (Biografie)" multiline rows={3} value={editData.biografie} onChange={e => setEditData({ ...editData, biografie: e.target.value })} fullWidth />
                                    {/* <TextField label="Bevorzugte Position (z.B. Stürmer)" value={editData.bevorzugte_position} onChange={e => setEditData({ ...editData, bevorzugte_position: e.target.value })} fullWidth /> */}
                                    <FormControl fullWidth>
                                        <InputLabel id="demo-simple-select-label">Bevorzugte Position</InputLabel>
                                        <Select
                                            value={editData.bevorzugte_position}
                                            label="Bevorzugte Position"
                                            fullWidth
                                            onChange={e => setEditData({ ...editData, bevorzugte_position: e.target.value })}
                                        >
                                            <MenuItem value={"Torwart"}>Torwart</MenuItem>
                                            <MenuItem value={"Verteidiger"}>Verteidiger</MenuItem>
                                            <MenuItem value={"Mittelfeld"}>Mittelfeld</MenuItem>
                                            <MenuItem value={"Sturm"}>Sturm</MenuItem>
                                            <MenuItem value={"Trainer"}>Trainer</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <TextField label="Stärkerer Fuß (links/rechts/beidfüßig)" value={editData.starker_fuss} onChange={e => setEditData({ ...editData, starker_fuss: e.target.value })} fullWidth />
                                    <TextField label="Bisherige Vereine" value={editData.bisherige_vereine} onChange={e => setEditData({ ...editData, bisherige_vereine: e.target.value })} fullWidth />
                                    <FormControlLabel
                                        control={<Switch checked={editData.kommentare_erlaubt} onChange={e => setEditData({ ...editData, kommentare_erlaubt: e.target.checked })} />}
                                        label="Kommentare auf Profil erlauben"
                                    />
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Typography><b>Über mich:</b> {user.biografie || '-'}</Typography>
                                    <Typography><b>Position:</b> {user.bevorzugte_position || '-'}</Typography>
                                    <Typography><b>Starker Fuß:</b> {user.starker_fuss || '-'}</Typography>
                                    <Typography><b>Bisherige Vereine:</b> {user.bisherige_vereine || '-'}</Typography>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Grid container spacing={3}>
                {/* Team Info */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Aktuelle Teams</Typography>
                            <Divider sx={{ mb: 2 }} />
                            {teams && teams.length > 0 ? (
                                teams.map((t, idx) => (
                                    <Box key={idx} sx={{ mb: 2 }}>
                                        <Typography variant="body1"><b>{t.team_name}</b></Typography>
                                        <Typography variant="body2">Rolle: {t.rolle} {t.trikotnummer ? `| Trikotnr: ${t.trikotnummer}` : ''}</Typography>
                                    </Box>
                                ))
                            ) : (
                                <Typography variant="body2" color="text.secondary">Kein Team zugewiesen</Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Kommentare */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card sx={{ height: '100%' }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Kommentare</Typography>
                            <Divider sx={{ mb: 2 }} />

                            {!user.kommentare_erlaubt ? (
                                <Typography variant="body2" color="text.secondary">Kommentare sind für dieses Profil deaktiviert.</Typography>
                            ) : (
                                <Box>
                                    <form onSubmit={handleAddComment} style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                                        <TextField size="small" fullWidth placeholder="Schreibe einen Kommentar..." value={newComment} onChange={e => setNewComment(e.target.value)} required />
                                        <Button variant="contained" type="submit" disabled={isCommenting}>Senden</Button>
                                    </form>

                                    {comments && comments.length > 0 ? (
                                        comments.map(c => (
                                            <Box key={c.id} sx={{ mb: 2, p: 1.5, bgcolor: 'background.default', borderRadius: "12px" }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="body2" fontWeight="bold">{c.first_name} {c.last_name}</Typography>
                                                    <Typography variant="caption" color="text.secondary">{new Date(c.created_at).toLocaleDateString()}</Typography>
                                                </Box>
                                                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{c.kommentar}</Typography>

                                                {/* Autor oder Profilbesitzer darf löschen */}
                                                {(c.autor_id === loggedInUser?.id || isOwnProfile) && (
                                                    <Button size="small" color="error" sx={{ minWidth: 0, p: 0, mt: 1 }} onClick={() => handleDeleteComment(c.id)}>Löschen</Button>
                                                )}
                                            </Box>
                                        ))
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">Noch keine Kommentare vorhanden.</Typography>
                                    )}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Profil;
